import { useMutation, useQuery } from "@tanstack/react-query";
import config from "./config";
import { ResponseError } from "./types/responseError";
import Cookies from "js-cookie";
import {
  PortfolioBalancesDto,
  PortfolioPerformanceResponseDto,
} from "./dtos/get-portfolio-data.dto";
import { PromotionDto } from "./dtos/promotion.dto";
import { OnboardingStatusDto } from "./dtos/onboarding-status.dto";
import { CashAccountResponseDto } from "./dtos/cash-account.dto";
import { GetAssetsResponseDto } from "./dtos/get-assets.dto";
import { GetAssetsSummaryResponseDto } from "./dtos/get-assets-summary.dto";
import { GetInProgressInvestmentsResponseDto } from "./dtos/get-in-progress-assets.dto";
import { GetAssetsPreviouslyOwnedResponseDto } from "./dtos/get-assets-previously-owned.dto";
import { GetFundBalancesResponseDto } from "./dtos/get-fund-balances.dto";
import useUserInfo from "../hooks/useUserInfo";

export const useGetPortfolioBalances = () => {
  const { investorId } = useUserInfo();

  return useQuery<PortfolioBalancesDto, ResponseError>({
    queryKey: ["investments", investorId],
    queryFn: async () => {
      const response = await fetch(
        `${config.SID_GW_BASE_URL}/v1/portfolio/balances`,
        {
          credentials: "include",
          headers: {
            Authorization: Cookies.get("auth-token") ?? "",
          },
        },
      );
      if (!response.ok) {
        console.error(await response.text());
        throw new ResponseError("Error fetching balances", response);
      }

      return response.json();
    },
  });
};

export const useGetPortfolioPerformance = () => {
  const { investorId } = useUserInfo();

  return useQuery<PortfolioPerformanceResponseDto, ResponseError>({
    queryKey: ["period", "last_90_days", investorId],
    queryFn: async () => {
      const response = await fetch(
        `${config.SID_GW_BASE_URL}/v1/portfolio/performance?period=last_90_days`,
        {
          credentials: "include",
          headers: {
            Authorization: Cookies.get("auth-token") ?? "",
          },
        },
      );
      if (!response.ok) {
        console.error(await response.text());
        throw new ResponseError("Error fetching asset performance", response);
      }

      return response.json();
    },
  });
};

export const useGetPromotions = () => {
  const { investorId } = useUserInfo();

  return useQuery<PromotionDto[], ResponseError>({
    queryKey: ["promotions", investorId],
    queryFn: async () => {
      const response = await fetch(`${config.SID_GW_BASE_URL}/v1/promotions`, {
        credentials: "include",
        headers: {
          Authorization: Cookies.get("auth-token") ?? "",
        },
      });

      if (!response.ok) {
        console.error(await response.text());
        throw new ResponseError("Error fetching promotions", response);
      }

      return response.json();
    },
  });
};

export const useGetOnboardingData = () => {
  const { investorId } = useUserInfo();

  return useQuery<OnboardingStatusDto, ResponseError>({
    queryKey: ["onboarding", investorId],
    queryFn: async () => {
      const response = await fetch(
        `${config.SID_GW_BASE_URL}/v1/portfolio/onboarding`,
        {
          credentials: "include",
          headers: {
            Authorization: Cookies.get("auth-token") ?? "",
          },
        },
      );
      if (!response.ok) {
        console.error(await response.text());
        throw new ResponseError("Error fetching onboarding data", response);
      }

      return response.json();
    },
  });
};

export const useGetCashAccount = () => {
  const { investorId } = useUserInfo();

  return useQuery<CashAccountResponseDto, ResponseError>({
    queryKey: ["cash-account", investorId],
    queryFn: async () => {
      const response = await fetch(
        `${config.SID_GW_BASE_URL}/v1/portfolio/cash-account`,
        {
          credentials: "include",
          headers: {
            Authorization: Cookies.get("auth-token") ?? "",
          },
        },
      );
      if (!response.ok) {
        console.error(await response.text());
        throw new ResponseError("Error fetching cash account data", response);
      }

      return response.json();
    },
  });
};

export const useGetAssets = () => {
  const { investorId } = useUserInfo();

  return useQuery<GetAssetsResponseDto, ResponseError>({
    queryKey: ["assets", investorId],
    queryFn: async () => {
      const response = await fetch(
        `${config.SID_GW_BASE_URL}/v1/portfolio/issued-assets`,
        {
          credentials: "include",
          headers: {
            Authorization: Cookies.get("auth-token") ?? "",
          },
        },
      );
      if (!response.ok) {
        console.error(await response.text());
        throw new ResponseError("Error fetching assets", response);
      }

      return response.json();
    },
  });
};

export const useGetAssetsSummary = () => {
  const { investorId } = useUserInfo();

  return useQuery<GetAssetsSummaryResponseDto, ResponseError>({
    queryKey: ["assets-summary", investorId],
    queryFn: async () => {
      const response = await fetch(
        `${config.SID_GW_BASE_URL}/v1/portfolio/assets-summary`,
        {
          credentials: "include",
          headers: {
            Authorization: Cookies.get("auth-token") ?? "",
          },
        },
      );
      if (!response.ok) {
        console.error(await response.text());
        throw new ResponseError("Error fetching assets", response);
      }

      return response.json();
    },
  });
};

export const useGetInProgressInvestments = () => {
  const { investorId } = useUserInfo();

  return useQuery<GetInProgressInvestmentsResponseDto, ResponseError>({
    queryKey: ["in-progress", investorId],
    queryFn: async () => {
      const response = await fetch(
        `${config.SID_GW_BASE_URL}/v1/portfolio/in-progress`,
        {
          credentials: "include",
          headers: {
            Authorization: Cookies.get("auth-token") ?? "",
          },
        },
      );
      if (!response.ok) {
        console.error(await response.text());
        throw new ResponseError("Error fetching assets", response);
      }

      return response.json();
    },
  });
};

export const useDeleteInvestment = () =>
  useMutation<GetInProgressInvestmentsResponseDto, ResponseError, number>({
    mutationFn: async (investmentId: number) => {
      const response = await fetch(
        `${config.SID_GW_BASE_URL}/v1/portfolio/in-progress/${investmentId}`,
        {
          method: "DELETE",
          credentials: "include",
          headers: {
            Authorization: Cookies.get("auth-token") ?? "",
          },
        },
      );
      if (!response.ok) {
        console.error(await response.text());
        throw new ResponseError("Error deleting investment", response);
      }

      return response.json();
    },
  });

export const useGetFundBalances = () => {
  const { investorId } = useUserInfo();

  return useQuery<GetFundBalancesResponseDto, ResponseError>({
    queryKey: ["fund-balances", investorId],
    queryFn: async () => {
      const response = await fetch(
        `${config.SID_GW_BASE_URL}/v1/portfolio/fund-balances`,
        {
          credentials: "include",
          headers: {
            Authorization: Cookies.get("auth-token") ?? "",
          },
        },
      );
      if (!response.ok) {
        const error = new ResponseError(
          "Error fetching fund balances",
          response,
        );
        console.error(
          `[${error.correlationId ?? "no-corr-id"}]`,
          await response.text(),
        );
        throw error;
      }

      return response.json();
    },
    enabled: config.FEATURE_FLAGS.PORTFOLIO_SFS_ASSETS_TAB_ENABLED,
  });
};

export const useGetPreviouslyOwnedAssets = () => {
  const { investorId } = useUserInfo();

  return useQuery<GetAssetsPreviouslyOwnedResponseDto, ResponseError>({
    queryKey: ["previously-owned", investorId],
    queryFn: async () => {
      const response = await fetch(
        `${config.SID_GW_BASE_URL}/v1/portfolio/previously-owned`,
        {
          credentials: "include",
          headers: {
            Authorization: Cookies.get("auth-token") ?? "",
          },
        },
      );
      if (!response.ok) {
        console.error(await response.text());
        throw new ResponseError(
          "Error fetching previously owned assets",
          response,
        );
      }

      return response.json();
    },
  });
};
